import _ from 'lodash';
import React, { useEffect, useState } from "react";
import DebounceInput from "react-debounce-input";
import NumberFormat from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { Alert, Button, Col, Input, Row, Spinner } from 'reactstrap';
import { CustomerTypeahead, FilterDateTime, SalesCallTrackingActionsGrid } from '.';
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, helpers } from "../utils";
import { toast } from "react-toastify";

const SAVED_MESSAGE = "Sales call saved.";
const SAVE_FAILED_MESSAGE = "The sales call could not be saved. If this persists, contact a system admin.";

export default function SalesCallTrackingEdit(props) {
  const navigate = useNavigate();
  const passedParams = useParams();
  const [actionList, setActionList] = useState([]);
  const [callData, setCallData] = useState({});
  const [editAction, setEditAction] = useState({});
  const [filters, setFilters] = useState([]);
  const [isProspect, setIsProspect] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [message, setMessage] = useState(null);
  const [salesCallActionId, setSalesCallActionId] = useState(null);
  const [salespeopleList, setSalespeopleList] = useState([]);
  const [salesTrackingActionTypes, setSalesTrackingActionTypes] = useState([]);
  const [showCallActionForm, setShowCallActionForm] = useState(false);
  const [trackingId, setTrackingId] = useState(passedParams.trackingId);

  useEffect(() => {
    let apiCalls = [];
    apiCalls.push(
      api.fetch(`Reference/GetSalespeopleList`).then((response) => {
        return {
          salespeople: response.data.salespeople
        };
      }).catch(api.catchHandler)
    );
    apiCalls.push(
      api.fetch(`Reference/GetAllSalesTrackingActionTypes`).then((response) => {
        return {
          salesTrackingActionTypes: response.data.salesTrackingActionTypes
        };
      }).catch(api.catchHandler)
    );
    Promise.all(apiCalls).then(arrayResults => {
      let aggResults = {};
      _.each(arrayResults, (x) => Object.assign(aggResults, x));
      setSalespeopleList(
        _.chain(aggResults.salespeople)
          .map((sp) => {
            return {
              value: sp.salespersonId,
              label: sp.salespersonText,
            };
          })
          .sortBy((x) => { return x.label })
          .unshift({
            value: 'All',
            label: '--- All ---'
          })
          .value()
      );
      setSalesTrackingActionTypes(
        _.chain(aggResults.salesTrackingActionTypes)
          .map((a) => {
            return {
              value: a.actionTypeId,
              label: a.description,
            };
          })
          .sortBy((x) => { return x.label })
          .value()
      );
    }).catch(error => setMessage({ flavor: "alert-danger", text: "There was an error loading sales people data" }));
  }, [])

  useEffect(() => {
    if (!salespeopleList.length || !salesTrackingActionTypes.length) return;
    getSalesCallData(trackingId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId, salespeopleList, salesTrackingActionTypes])

  useEffect(() => {
    if (showCallActionForm === false)
      setEditAction({})
  }, [showCallActionForm])

  useEffect(() => {
    // if (salespeopleList.length === 0) return;
    getCallTrackingActionsData(salesCallActionId);
    setSalesCallActionId(salesCallActionId)
  }, [salesCallActionId])

  useEffect(() => {
    if (passedParams.trackingId !== trackingId) {
      setTrackingId(passedParams.trackingId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedParams.trackingId])

  function ColHeader({ name, textColor }) {
    return (
      <Col xs={3} lg={2} className="text-end">
        <h4 className={`pt-1 ${textColor}`}>
          {name}
        </h4>
      </Col>
    )
  }

  function createActionPayload(pTrackingId) {
    let payload = {
      SortField: null,
      SortDirection: null,
    }
    _.map(filters, (filter) => {
      payload[filter.filterName] = filter.value;
    });
    if (payload.actionDate === "") {
      payload.actionDate = null;
    }
    if (payload.followUpDate === "") {
      payload.followUpDate = currentDate;
    }
    // if (payload.salesCallActionId > 0 
    //   ? payload.salesCallActionId = editAction.salesCallActionId.value 
    //   : 0);
    payload.salesCallActionId = salesCallActionId;
    payload.purposeOfCall = editAction.purposeOfCall;
    payload.notes = editAction.notes;
    payload.actionItems = editAction.actionItems;
    payload.outlookReminder = editAction.outlookReminder;
    payload.trackingId = pTrackingId ? pTrackingId : callData.trackingId;
    return payload;
  }

  function createSalesCallPayload() {
    let payload = {};
    payload.trackingId = trackingId;
    payload.actionTypeId = callData.actionTypeId ? callData.actionTypeId.value : null;
    payload.salespersonId = callData.salesperson ? callData.salesperson.value : null;
    if (callData.customer) {
      payload.customerId = callData.customer.value;
    }
    delete callData.customer;

    if (isProspect === true) {
      payload.customerId = "other";
    }
    delete callData.customer;
    payload.customerOther = callData.customerOther;
    payload.industry = callData.industry ? callData.industry.value : null;
    payload.contact = callData.contact;
    payload.contactTitle = callData.contactTitle;
    payload.contactPhone = callData.contactPhone;
    payload.emailAddress = callData.emailAddress || "";
    payload.companyWebSite = callData.companyWebSite;
    payload.isoRegistered = callData.isoRegistered;
    payload.otherCertifications = callData.otherCertifications;
    payload.associationMemberships = callData.associationMemberships;

    return payload;
  }

  function currentDate() {
    let date = new Date();
    let month = date.getMonth() + 1;
    let day = date.getDate();
    let year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  function getCallTrackingActionsData(salesCallActionId) {
    if (!parseInt(salesCallActionId, 10)) return; // if it's new there is nothing to go get
    setLoaded(false);
    api
      .post(`order/GetSalesCallTrackingActions`, { salesCallActionId: salesCallActionId })
      .then(response => {
        if (response.data.success) {
          let newActionData = response.data.trackingAction;
          //newActionData.outlookReminder = newActionData.outlookReminder; // assigned to itself  no-self-assign
          setEditAction(newActionData);
          setSalesCallActionId(newActionData.salesCallActionId)
          setShowCallActionForm(true)
        }
      })
      .catch(api.catchHandler)
      .finally(() => setLoaded(true))
  }

  function getSalesCallData(trackingId) {
    if (!parseInt(trackingId, 10)) return; // if it's new there is nothing to go get
    setLoaded(false);
    api
      .post(`order/GetSalesCallTracking`, { trackingId: trackingId })
      .then(response => {
        if (response.data.success) {
          let newData = response.data.salesCall;
          newData.actionTypeId = _.find(salesTrackingActionTypes, t => t.value === newData.actionTypeId);
          newData.industry = _.find(constants.INDUSTRY_TYPES, t => t.label === newData.industry);
          newData.salesperson = _.find(salespeopleList, t => t.value === newData.salespersonId);
          newData.customer = { label: newData.customerId, value: newData.customerId };
          setCallData(newData);
          getSalesCallTrackingActions(newData.trackingId);
        }
      })
      .catch(api.catchHandler)
      .finally(() => setLoaded(true))
  }

  function getSalesCallTrackingActions(trackingId) {
    api
      .post(`order/GetSalesCallTrackingActionsByTrackingId`, createActionPayload(trackingId))
      .then(response => {
        if (response.data.success) {
          setActionList(response.data.trackingActionsList);
          setSalesCallActionId(response.data.salesCallActionId)
        }
      })
      .catch(api.catchHandler)
  }

  function isAProspect() {
    setIsProspect(true)
  }

  function onCallChange(fieldName, fieldValue) {
    setCallData({
      ...callData,
      [fieldName]: fieldValue
    });
  }

  function onChangeAction(fieldName, fieldValue) {
    setEditAction({
      ...editAction,
      [fieldName]: fieldValue
    });
  }

  function onFilterChange(changedFilter) {
    const filterChanges = filterHelpers.getFilters(filters, changedFilter);
    setFilters(filterChanges);
    // setLoaded(false)
  }

  function printSalesCall(trackingId) {
    let payload = { trackingId: trackingId }
    // setDownloading(true);
    api
      .postDownloadBinary("Report/SalesCallReport", payload, constants.MIME_TYPES.pdf, `SalesCallReport_${currentDate()}.pdf`)
      .then((res) => {
        if (res?.data === false) {
          toast.error(res.message);
        }
      })
      .catch(api.catchHandler);
    // .finally(() => setDownloading(false));
  }

  function saveSalesCallAction() {
    api
      .post(`order/SalesCallTrackingActionsSave`, createActionPayload())
      .then(response => {
        if (response.data.success) {
          getSalesCallTrackingActions(trackingId)
          setShowCallActionForm(false)
          setMessage(null);
        } else {
          setMessage({
            text: response.data.message,
            flavor: "danger",
          });
        }
      })
      .catch(api.catchHandler)
  }

  // The one save path behind all three save buttons. The server answers every successful save - new call or edit - with
  // the call's id (newTrackingId), and a refusal with { success: false, message }. Each button used to act only when the
  // id CHANGED, so an edit saved silently and "Save and Return" / "Save and Print" did nothing for an existing call.
  function saveSalesCall(afterSave) {
    if (!validateEntry()) return;
    api
      .post(`order/SalesCallTrackingSave`, createSalesCallPayload())
      .then(response => {
        if (!response?.data) {
          // an HTTP failure resolves with no data; the shared interceptor has already told the user about some of them
          if (!api.isHandledByInterceptor(response)) toast.error(SAVE_FAILED_MESSAGE);
          return;
        }
        if (!response.data.success) {
          toast.error(response.data.message || SAVE_FAILED_MESSAGE);
          return;
        }
        const savedTrackingId = response.data.newTrackingId || trackingId;
        // trackingId comes from the URL (a string) on an edit; the server returns a number
        if (String(savedTrackingId) !== String(trackingId)) setTrackingId(savedTrackingId);
        toast.success(SAVED_MESSAGE);
        if (afterSave) afterSave(savedTrackingId);
      })
      .catch(api.catchHandler)
  }

  function saveSalesCallTracking() {
    saveSalesCall();
  }

  function saveSalesCallTrackingAndPrint() {
    saveSalesCall((savedTrackingId) => printSalesCall(savedTrackingId));
  }

  function saveSalesCallTrackingAndReturn() {
    saveSalesCall(() => navigate(constants.ENDPOINTS.salesCallTracking));
  }

  function validateEntry() {
    let warnings = [];
    if (callData.emailAddress && !helpers.emailIsValid(callData.emailAddress)) {
      warnings.push("A valid email is required.");
    }
    if (!callData.salesperson || !callData.salesperson.value) {
      warnings.push("Salesperson is required.");
    }
    if (!callData.actionTypeId) {
      warnings.push("Contact type is required.");
    }
    if (warnings.length) {
      setMessage({
        text: warnings.join(" "),
        flavor: "danger",
      });
      return false;
    }
    setMessage(null);
    return true;
  }

  return (
    <DesktopContainer screenName="Sales Call Tracking Edit">
      <div className="filter-header">
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Sales Call #:" />
          <Col xs={6}>
            {trackingId ?
              <h4 className="pt-1 text-white">
                {trackingId}
              </h4>
              : null}
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Sales Person:" />
          <Col xs={6}>
            <Select
              classNamePrefix="serviceThreadSelect"
              closeMenuOnSelect={true}
              components={makeAnimated()}
              options={salespeopleList}
              value={callData.salesperson}
              onChange={(sp) => onCallChange('salesperson', sp)}
            />
          </Col>
        </Row>
        {isProspect === false ?
          <Row className="justify-content-center mb-2">
            <ColHeader textColor="text-white" name="Customer:" />
            <Col xs={6}>
              {trackingId >= 1 /* Had to change this back to 1, because tracking id is being saved as string, so it sees 0 as existing*/
                ? <h4 className="pt-1 text-white">
                  {callData.customer ? callData.customer.label : null}
                </h4>
                : (
                  <div style={{ display: "flex" }}>
                    <Col xs={9}>
                      <CustomerTypeahead
                        value={callData.customer}
                        onChange={(c) => onCallChange('customer', c)}
                      />
                    </Col>
                    <Col xs={3}>
                      <Button className="shredButtonGray" style={{ marginLeft: "20px" }}
                        onClick={() => isAProspect()}>Is a Prospect?</Button>
                    </Col>
                  </div>)
              }
            </Col>
          </Row>
          :
          <>
            <Row className="justify-content-center mb-2">
              <ColHeader textColor="text-white" name="Customer:" />
              <Col xs={6}>
                {trackingId >= 1 /* Had to change this back to 1, because tracking id is being saved as string, so it sees 0 as existing*/
                  ? <h4 className="pt-1 text-white">
                    Customer:
                  </h4>
                  : (<div style={{ display: "flex" }}>
                    <Col xs={9}>
                      <h4 className="pt-1 text-white">Other</h4>
                    </Col>
                    <Col xs={3}>
                      <Button
                        className="shredButtonLower"
                        style={{ marginLeft: "20px" }}
                        onClick={() => setIsProspect(false)}>
                        Is a Customer?
                      </Button>
                    </Col>
                  </div>)
                }
              </Col>
            </Row>
            <Row className="justify-content-center mb-2">
              <ColHeader textColor="text-white" name="Prospect Name:" />
              <Col xs={6}>
                {trackingId >= 1 /* Had to change this back to 1, because tracking id is being saved as string, so it sees 0 as existing*/
                  ? <h4 className="pt-1 text-white">
                    {callData.customerOther}
                  </h4>
                  :
                  <Col xs={12}>
                    <DebounceInput
                      type="text"
                      value={callData.customerOther}
                      onChange={(e) => onCallChange('customerOther', e.target.value)}
                      debounceTimeout={300}
                      className="float-start form-control"
                    />
                  </Col>
                }
              </Col>
            </Row>
          </>
        }
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Contact Type:" />
          <Col xs={6}>
            <Select
              classNamePrefix="serviceThreadSelect"
              closeMenuOnSelect={true}
              components={makeAnimated()}
              options={salesTrackingActionTypes}
              value={callData.actionTypeId}
              onChange={(a) => onCallChange('actionTypeId', a)}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Contact Name:" />
          <Col xs={6}>
            <DebounceInput
              type="text"
              value={callData.contact}
              onChange={(e) => onCallChange('contact', e.target.value)}
              debounceTimeout={300}
              className="float-start form-control"
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Contact Title:" />
          <Col xs={6}>
            <DebounceInput
              type="text"
              value={callData.contactTitle}
              onChange={(e) => onCallChange('contactTitle', e.target.value)}
              debounceTimeout={300}
              className="float-start form-control"
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Contact Phone:" />
          <Col xs={6}>
            <NumberFormat
              className="form-control"
              format="(###) ###-####"
              mask="_"
              name="contactPhone"
              id="contactPhone"
              maxLength="15"
              value={callData.contactPhone}
              onChange={(e) => onCallChange('contactPhone', e.target.value)}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Email Address:" />
          <Col xs={6}>
            <DebounceInput
              type="text"
              value={callData.emailAddress}
              onChange={(e) => onCallChange('emailAddress', e.target.value)}
              debounceTimeout={300}
              className="float-start form-control"
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Company Website:" />
          <Col xs={6}>
            <DebounceInput
              type="text"
              value={callData.companyWebSite}
              onChange={(e) => onCallChange('companyWebSite', e.target.value)}
              debounceTimeout={300}
              className="float-start form-control"
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="ISO Registered:" />
          <Col xs={6} style={{ display: "flex", alignItems: "center" }}>
            <Input
              style={{ backgroundColor: "#f76f1e" }}
              type="checkbox"
              checked={callData.isoRegistered}
              onChange={() => onCallChange('isoRegistered', !callData.isoRegistered)}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Other Certifications:" />
          <Col xs={6}>
            <DebounceInput
              type="text"
              value={callData.otherCertifications}
              onChange={(e) => onCallChange('otherCertifications', e.target.value)}
              debounceTimeout={300}
              className="float-start form-control"
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Industry:" />
          <Col xs={6}>
            <Select
              classNamePrefix="serviceThreadSelect"
              closeMenuOnSelect={true}
              components={makeAnimated()}
              options={constants.INDUSTRY_TYPES}
              value={callData.industry}
              onChange={(i) => onCallChange('industry', i)}
            />
          </Col>
        </Row>
        <Row className="justify-content-center mb-2">
          <ColHeader textColor="text-white" name="Memberships:" />
          <Col xs={6}>
            <DebounceInput
              type="text"
              value={callData.associationMemberships}
              onChange={(e) => onCallChange('associationMemberships', e.target.value)}
              debounceTimeout={300}
              className="float-start form-control"
            />
          </Col>
        </Row>
      </div>
      <div>
        {message ? (
          <Alert className={message.flavor}>{message.text}</Alert>
        ) : null}
        <Row className="align-items-center my-3">
          <Col xs={6} className="text-end">
            {trackingId <= 0 ? null :
              <h3
                className="pt-1 text-blue"
                children="Call Actions"
              />
            }
          </Col>
          {trackingId <= 0 ? null :
            <Col xs={4}>
              {showCallActionForm === true ?
                <Button
                  className="shredButton"
                  style={{ float: "right" }}
                  onClick={() => setShowCallActionForm(false)}>
                  Cancel
                </Button>
                :
                <Button className="shredButton"
                  onClick={() => setShowCallActionForm(true)}>
                  Add New Action
                </Button>
              }
            </Col>
          }
        </Row>
        {showCallActionForm === true ?
          <>
            <Row>
              <Col xs={4}>
                <h4
                  children="Action Date:"
                  className="float-end text-blue"
                />
              </Col>
              <Col xs={2}>
                <FilterDateTime
                  filterName="actionDate"
                  value={filterHelpers.getValue(filters, "actionDate")}
                  onChangeCallback={(e) => onFilterChange(e)}
                />
              </Col>
              <Col xs={2}>
                <h4
                  children="Follow Up Date:"
                  className="float-end text-blue"
                />
              </Col>
              <Col xs={2}>
                <FilterDateTime
                  filterName="followUpDate"
                  value={filterHelpers.getValue(filters, "followUpDate")}
                  onChangeCallback={(e) => onFilterChange(e)}
                />
              </Col>
            </Row>
            <Row className="align-items-center my-1">
              <Col xs={4} className="text-end">
                <h4
                  className="pt-1 text-blue"
                  children="Outlook Reminder:"
                />
              </Col>
              <Col xs={6}>
                <Input
                  style={{ backgroundColor: "#f76f1e" }}
                  type="checkbox"
                  checked={editAction.outlookReminder}
                  onChange={() => onChangeAction('outlookReminder', !editAction.outlookReminder)}
                />
              </Col>
            </Row>
            <Row className="align-items-center my-1">
              <Col xs={4} className="text-end">
                <h4
                  className="pt-1 text-blue"
                  children="Purpose of Call:"
                />
              </Col>
              <Col xs={6}>
                <DebounceInput
                  type="text"
                  value={editAction.purposeOfCall}
                  onChange={(e) => onChangeAction('purposeOfCall', e.target.value)}
                  debounceTimeout={300}
                  className="float-start form-control"
                />
              </Col>
            </Row>
            <Row className="align-items-center my-1">
              <Col xs={4} className="text-end">
                <h4
                  className="pt-1 text-blue"
                  children="Call Notes:"
                />
              </Col>
              <Col xs={6}>
                <DebounceInput
                  type="text"
                  value={editAction.notes}
                  onChange={(e) => onChangeAction('notes', e.target.value)}
                  debounceTimeout={300}
                  className="float-start form-control"
                />
              </Col>
            </Row>
            <Row className="align-items-center my-1">
              <Col xs={4} className="text-end">
                <h4
                  className="pt-1 text-blue"
                  children="Action Items:"
                />
              </Col>
              <Col xs={6}>
                <DebounceInput
                  type="text"
                  value={editAction.actionItems}
                  onChange={(e) => onChangeAction('actionItems', e.target.value)}
                  debounceTimeout={300}
                  className="float-start form-control"
                />
              </Col>
              <Col>
                <Button
                  className="shredButton"
                  onClick={saveSalesCallAction}
                  style={{ margin: "5px" }}>
                  Update Call Action
                </Button>
              </Col>
            </Row>
          </> : null}
        <Row style={{ paddingTop: "10px" }}>
          {loaded && actionList ?
            <SalesCallTrackingActionsGrid
              actionFunction={getCallTrackingActionsData}
              setAction={setEditAction}
              data={actionList}
            />
            : <Spinner />}
        </Row>
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "10px" }}>
          <Button
            className="shredButton"
            onClick={saveSalesCallTracking}
            style={{ margin: "5px" }}>
            Save Sales Call
          </Button>
          <Button className="shredButton" style={{ margin: "5px" }} onClick={saveSalesCallTrackingAndPrint} >Save And Print</Button>
          <Button className="shredButton" style={{ margin: "5px" }} onClick={saveSalesCallTrackingAndReturn}>Save And Return to Search</Button>
          <Button style={{ margin: "5px" }} onClick={() => navigate(constants.ENDPOINTS.salesCallTracking)}>Cancel</Button>
        </div>
      </div>
    </DesktopContainer>
  )
}
